import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Group,
  Matrix4,
  Mesh,
  MeshLambertMaterial,
} from 'three';
import { IFC_CONFIG } from '../config';
import type { GeometryBatch, TransferGeometry } from './geometryTransfer';
import type { WorkerRequest, WorkerResponse } from './ifcWorker';
import type { ModelTreeNode } from './modelTree';
import type { PropertyGroup } from './propertyExtraction';

export type { PropertyEntry, PropertyGroup } from './propertyExtraction';

export interface LoadProgress {
  phase: 'init' | 'parse' | 'geometry' | 'done';
  message: string;
  percent?: number;
}

export type ProgressCallback = (p: LoadProgress) => void;

export interface LoadResult {
  group: Group;
  /** Number of parser issues web-ifc reported while reading the file. */
  warningCount: number;
}

interface Pending {
  resolve: (payload: unknown) => void;
  reject: (err: Error) => void;
}

interface ActiveLoad {
  group: Group;
  onProgress: ProgressCallback | undefined;
}

/**
 * Main-thread client for the IFC worker: wasm parsing and geometry
 * generation happen off-thread, mesh and material construction here.
 */
export class IFCLoader {
  private worker: Worker | null = null;
  private nextRequestId = 1;
  private pending = new Map<number, Pending>();
  private loads = new Map<number, ActiveLoad>();
  private materialCache = new Map<string, MeshLambertMaterial>();
  private geometryCache = new Map<number, BufferGeometry>();
  private lengthUnit: string | null = null;

  async init(): Promise<void> {
    if (this.worker) return;
    const worker = new Worker(new URL('./ifcWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => this.onMessage(e.data);
    worker.onerror = (e: ErrorEvent) => {
      const error = new Error(e.message || 'IFC worker crashed');
      for (const p of this.pending.values()) p.reject(error);
      this.pending.clear();
      this.loads.clear();
    };
    this.worker = worker;
    await this.request({ type: 'init', wasmPath: IFC_CONFIG.wasmPath });
  }

  async load(buffer: ArrayBuffer, onProgress?: ProgressCallback): Promise<LoadResult> {
    if (!this.worker) throw new Error('IFCLoader not initialized; call init() first');
    this.geometryCache = new Map();

    onProgress?.({ phase: 'parse', message: 'Parsing IFC…', percent: 10 });
    const group = new Group();
    const id = this.nextRequestId;
    this.loads.set(id, { group, onProgress });
    try {
      const payload = (await this.request({ type: 'load', buffer, coordinateToOrigin: IFC_CONFIG.coordinateToOrigin }, [
        buffer,
      ])) as { warningCount: number; lengthUnit: string | null };
      this.lengthUnit = payload.lengthUnit;
      group.name = 'ifc-model';
      onProgress?.({ phase: 'done', message: `Loaded ${group.children.length} meshes`, percent: 100 });
      return { group, warningCount: payload.warningCount };
    } finally {
      this.loads.delete(id);
    }
  }

  async getProperties(expressID: number): Promise<PropertyGroup[]> {
    if (!this.worker) return [];
    return (await this.request({ type: 'getProperties', expressID })) as PropertyGroup[];
  }

  async getModelTree(): Promise<ModelTreeNode | null> {
    if (!this.worker) return null;
    return (await this.request({ type: 'getModelTree' })) as ModelTreeNode | null;
  }

  /** Display label of the model's length unit (e.g. "mm"), if detected. */
  get unit(): string | null {
    return this.lengthUnit;
  }

  private request(req: Omit<WorkerRequest, 'id'>, transfer: Transferable[] = []): Promise<unknown> {
    const worker = this.worker;
    if (!worker) return Promise.reject(new Error('Worker not running'));
    const id = this.nextRequestId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      worker.postMessage({ id, ...req } satisfies WorkerRequest, transfer);
    });
  }

  private onMessage(msg: WorkerResponse): void {
    switch (msg.type) {
      case 'result': {
        const p = this.pending.get(msg.id);
        this.pending.delete(msg.id);
        p?.resolve(msg.payload);
        break;
      }
      case 'error': {
        const p = this.pending.get(msg.id);
        this.pending.delete(msg.id);
        p?.reject(new Error(msg.message));
        break;
      }
      case 'progress': {
        const load = this.loads.get(msg.id);
        const percent = msg.total > 0 ? 15 + (msg.processed / msg.total) * 80 : undefined;
        load?.onProgress?.({
          phase: 'geometry',
          message:
            msg.total > 0
              ? `Building geometry… ${msg.processed} / ${msg.total} elements`
              : `Built ${msg.processed} elements…`,
          ...(percent !== undefined ? { percent } : {}),
        });
        break;
      }
      case 'geometry': {
        const load = this.loads.get(msg.id);
        if (load) this.addBatch(load.group, msg.batch);
        break;
      }
    }
  }

  private addBatch(group: Group, batch: GeometryBatch): void {
    for (const g of batch.geometries) {
      this.geometryCache.set(g.geometryID, buildGeometry(g));
    }
    const matrix = new Matrix4();
    for (const placement of batch.placements) {
      const geometry = this.geometryCache.get(placement.geometryID);
      if (!geometry) continue;
      const mesh = new Mesh(geometry, this.getMaterial(placement.color));
      matrix.fromArray(placement.matrix);
      mesh.applyMatrix4(matrix);
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      mesh.userData['expressID'] = placement.expressID;
      mesh.name = `ifc-${placement.expressID}`;
      group.add(mesh);
    }
  }

  private getMaterial(color: { x: number; y: number; z: number; w: number }): MeshLambertMaterial {
    const key = `${color.x.toFixed(3)}_${color.y.toFixed(3)}_${color.z.toFixed(3)}_${color.w.toFixed(3)}`;
    let mat = this.materialCache.get(key);
    if (mat) return mat;

    const transparent = color.w < 1;
    mat = new MeshLambertMaterial({
      color: new Color(color.x, color.y, color.z),
      transparent,
      opacity: color.w,
      side: DoubleSide,
      depthWrite: !transparent,
    });
    this.materialCache.set(key, mat);
    return mat;
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    for (const p of this.pending.values()) p.reject(new Error('IFCLoader disposed'));
    this.pending.clear();
    this.loads.clear();
    for (const mat of this.materialCache.values()) mat.dispose();
    this.materialCache.clear();
    this.geometryCache.clear();
    this.lengthUnit = null;
  }
}

function buildGeometry(g: TransferGeometry): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(g.positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(g.normals, 3));
  geometry.setIndex(new BufferAttribute(g.indices, 1));
  return geometry;
}
